import {Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import { AuthService } from './security/auth.service';
import { Router } from '@angular/router';
import {LocalStorageService} from "./services/local-storage.service";
import {Location} from "@angular/common";
import {PATH_BEFORE_REFRESH, RoleID, SUBMISSION_PATH} from "./common/Constants";
import {UserService} from "./services/user.service";
import {TitleService} from "./services/title.service";
import {PostHogService} from "./services/telemetry/posthog.service";
import {Subscription} from "rxjs";

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  isAuthenticated: boolean;
  private authSubscription: Subscription;
  private userSubscription: Subscription;

  private wasAuthenticated = false;

  @HostListener('window:beforeunload', ['$event'])
  doSomething($event) {
    if(this.location.path() != '/login')
      this.localStorageService.set(PATH_BEFORE_REFRESH, this.location.path())
  }

  constructor(
    private router: Router,
    private authService: AuthService,
    private localStorageService: LocalStorageService,
    private location: Location,
    private userService: UserService,
    private titleService: TitleService,
    private posthogService: PostHogService
  ) {
    this.isAuthenticated = false;
  }

  ngOnInit(): void {
    // Subscribe to authentication state changes
    this.authSubscription = this.authService.authenticationSubject.subscribe(isAuthenticated => {
      this.isAuthenticated = isAuthenticated;
      this.titleService.setTitle(isAuthenticated);

      if (isAuthenticated) {
        this.wasAuthenticated = true;
        // Start telemetry only after authenticated login (not on public e-sign pages)
        this.posthogService.init();

        // Identify immediately if user profile is already in memory
        if (this.authService.loggedInUser) {
          this.identifyPostHogUser(this.authService.loggedInUser);
        }

        // Also subscribe to user profile stream to identify once async profile resolves
        if (this.authService.userDataManager?.user$) {
          if (this.userSubscription) {
            this.userSubscription.unsubscribe();
          }
          this.userSubscription = this.authService.userDataManager.user$.subscribe((user) => {
            this.identifyPostHogUser(user);
          });
        }
      } else if (this.wasAuthenticated) {
        // Reset user session only on explicit logout, not on initial reload
        this.wasAuthenticated = false;
        if (this.userSubscription) {
          this.userSubscription.unsubscribe();
        }
        this.posthogService.resetUser();
      }
    });

    // Initial authentication check on page reload
    this.authService.isAuthenticated().then((success: boolean) => {
      this.isAuthenticated = success;
      this.titleService.setTitle(success);
      if (success) {
        this.wasAuthenticated = true;
        this.posthogService.init();
        if (this.authService.loggedInUser) {
          this.identifyPostHogUser(this.authService.loggedInUser);
        }
      }
    }).catch(() => {
      this.isAuthenticated = false;
      this.titleService.setTitle(false);
    });
  }

  private identifyPostHogUser(user: { id?: string; aws_cognito_id?: string; currentUserRole?: string; defaultRole?: string } | null | undefined): void {
    if (!user) return;
    const opaqueUserId = user?.id || user?.aws_cognito_id;
    if (opaqueUserId) {
      this.posthogService.identifyUser(opaqueUserId, {
        role: user?.currentUserRole || user?.defaultRole || 'user',
      });
    }
  }

  ngOnDestroy(): void {
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
  }
}
